import { scaleLinear, scaleTime } from "d3-scale"
import { line, curveLinear } from "d3-shape"
import { axisBottom, axisLeft } from "d3-axis"
import moment from "moment"
import { find, last, first, get, isFinite, chain, isNil, isNull, filter, size, min, forEach, clamp } from "lodash"
import { createSelector } from "reselect"
import { calculateHighlighter } from "../helpers/highlighter"
import { xAxisTicks } from "../helpers/x-axis-ticks"

const MARGINS = { top: 15, left: 50, right: 0, bottom: 15 }
const THRESHOLD_CLASSES = ["threshold-low", "threshold-high"]

export const getData = props => props.data
const getDate = props => props.date
const getIsPeriodIntraday = props => props.isPeriodIntraday
const getWidth = props => props.width || 150
const getStartDate = props => props.startDate
const getEndDate = props => props.endDate
const getHeight = props => props.height
const getItems = props => props.items
const getTickFormat = props => props.tickFormat
const getOnly = props => props.onlyItem
const getThresholdParams = props => props.thresholdParams
const getNumYTicks = props => props.numYTicks

const getFilteredItems = createSelector(
  [getItems],
  (items) => items.filter(item => !item.disabled),
)

export const getOnlyLegendText = createSelector(
  [getOnly],
  (only) => only ? "All" : "Only",
)

const getMinX = createSelector(
  [getStartDate, getData],
  (startDate, data) => {
    if (data && data.length > 0) return first(data).date.toDate()
    return moment(startDate).toDate()
  },
)

const getMaxX = createSelector(
  [getEndDate, getData],
  (endDate, data) => {
    if (data && data.length > 0) return last(data).date.toDate()
    return moment(endDate).toDate()
  },
)

const getXTicks = createSelector(
  [getMinX, getMaxX],
  xAxisTicks,
)

const getMinY = createSelector(
  [getData, getFilteredItems],
  (data, items) => data.reduce((min, datum) => {
    return Math.min(min, ...items.map(({ id }) => datum[id]).filter(datum => isFinite(datum)))
  }, Infinity),
)

const getMaxY = createSelector(
  [getData, getFilteredItems],
  (data, items) => data.reduce((max, datum) => {
    return Math.max(max, ...items.map(({ id }) => datum[id]).filter(datum => isFinite(datum)))
  }, -Infinity),
)

export const getScaleX = createSelector(
  [getMinX, getMaxX, getWidth],
  (minX, maxX, width) => scaleTime()
    .domain([minX, maxX])
    .rangeRound([MARGINS.left, width - MARGINS.right])
    .clamp(true),
)

export const getScaleY = createSelector(
  [getMinY, getMaxY, getHeight],
  (minY, maxY, height) => {
    return scaleLinear()
      .domain([minY, maxY])
      .rangeRound([height - MARGINS.bottom, MARGINS.top])
  },
)

export const getLines = createSelector(
  [getScaleX, getScaleY, getData, getFilteredItems],
  (scaleX, scaleY, data, items) => {
    const lines = chain(items)
      .keyBy(item => item.id)
      .mapValues(item => {
        const points = chain(data)
          .map(d => ({
            date: d.date,
            [item.id]: d[item.id],
            index: d.index,
          }))
          .value()
        const filtered = filter(points, (p) => !isNull(p[item.id]))
        const missing = filter(points, (p) => !isNil(p[item.id]))
        // If filtered and missing have the same length, there is no missing data
        if (size(filtered) === size(missing)) {
          return { filtered }
        }

        // If the first point in the missing line is not index 0, than we need to add fake points
        // in order for d3 to be able to draw a line from the beginning
        let corrected = missing
        const added = []
        const firstDot = first(missing)
        if (firstDot.index !== 0) {
          for (let i = 0; i < firstDot.index; i++) {
            added.push({
              date: get(data, [i, "date"]),
              [item.id]: 0,
              index: i,
            })
          }
          corrected = [
            ...added,
            ...corrected,
          ]
        }

        return {
          filtered,
          missing: first(missing).index === 0 ? missing : corrected,
        }
      })
      .value()

    return items
      .map(({ id, color }) => {
        const obj = ({
          id,
          d: line()
            .x(d => scaleX(d.date))
            .y(d => scaleY(get(d, id)))
            .defined(d => !isNil(d[id]))
            .curve(curveLinear)(lines[id].filtered),
          style: { stroke: color },
        })
        // If we have missing data line points, return a line object
        if (lines[id].missing) {
          obj.missing = line()
            .x(d => scaleX(d.date))
            .y(d => scaleY(get(d, id)))
            .curve(curveLinear)(lines[id].missing)
        }
        return obj
      })
  },
)

export const getBoundaries = createSelector(
  [getHeight, getWidth],
  (height, width) => ({
    left: MARGINS.left,
    right: width - MARGINS.right,
    top: MARGINS.top,
    bottom: height - MARGINS.bottom,
  }),
)

export const getXAxis = createSelector(
  [getBoundaries, getHeight, getScaleX, getXTicks],
  (boundaries, height, scaleX, [ticks, formatter]) => ({
    fn: axisBottom()
      .scale(scaleX)
      .ticks(ticks)
      .tickSize(-height + MARGINS.top + MARGINS.bottom)
      .tickFormat(formatter),
    transform: `translate(0,${boundaries.bottom})`,
    baseline: {
      x1: boundaries.left,
      x2: boundaries.right,
      y1: boundaries.bottom,
      y2: boundaries.bottom,
    },
  }),
)

export const getYAxis = createSelector(
  [getBoundaries, getWidth, getScaleY, getTickFormat, getNumYTicks],
  (boundaries, width, scaleY, tickFormat, yTicks) => ({
    fn: axisLeft()
      .scale(scaleY)
      .tickSize(-width + MARGINS.left + MARGINS.right)
      .tickFormat(tickFormat)
      .ticks(yTicks),
    transform: `translate(${boundaries.left},0)`,
    baseline: {
      x1: boundaries.left,
      x2: boundaries.left,
      y1: boundaries.top,
      y2: boundaries.bottom,
    },
  }),
)

export const getFindByOffset = createSelector(
  [getData, getScaleX],
  (data, scaleX) => offset => {
    const lookup = scaleX.invert(offset - 18) // -2 is to give a margin of selection on the first element, -16 for the margin
    return find(data, datum => datum.date >= lookup)
  },
)

export const getHighlighter = createSelector(
  [getDate, getScaleX, getScaleY, getMinX, getMaxX, getMinY, getMaxY, getIsPeriodIntraday],
  // The selected-date highlighter is a multi-day affordance; intraday has no date selection to highlight.
  (date, scaleX, scaleY, minX, maxX, minY, maxY, isPeriodIntraday) =>
    isPeriodIntraday ? null : calculateHighlighter(date, scaleX, scaleY, minX, maxX, minY, maxY),
)

function computeThreshold(startPosition, endPosition, { left, right }, level) {
  return {
    y: min([startPosition, endPosition]),
    height: Math.abs(startPosition - endPosition),
    x: left,
    width: right + 1 - left,
    className: THRESHOLD_CLASSES[Math.abs(level) - 1],
  }
}

export const getThresholds = createSelector(
  [getScaleY, getMinY, getMaxY, getThresholdParams, getBoundaries],
  (scaleY, minY, maxY, thresholdParams, boundaries) => {
    const thresholds = []
    if (!thresholdParams) {
      return thresholds
    }

    const { value: thresholdValue } = thresholdParams

    // Add positive thresholds from top downwards
    let currentPosition = boundaries.top
    forEach([2, 1, -2, -1], level => {
      const value = thresholdValue * level
      if ((value > 0 && value < maxY) || (value < 0 && value > minY)) {
        const position = scaleY(clamp(value, minY, maxY))
        thresholds.push(computeThreshold(currentPosition, position, boundaries, level))
        currentPosition = position
      }

      // Add negative thresholds from bottom upwards
      if (level === 1) {
        currentPosition = boundaries.bottom
      }
    })

    return thresholds
  },
)
